import React from 'react';
import { ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { getPublicPlatformBillingCatalog } from '@/lib/api/platformBilling';
import type { CreditUsageGuide } from '@/types/platformBilling';

export function CohortCreditGuide({
  usage,
  loadCatalog = false,
  collapsible = false,
}: {
  usage?: CreditUsageGuide | null;
  loadCatalog?: boolean;
  collapsible?: boolean;
}) {
  const [loaded, setLoaded] = React.useState<CreditUsageGuide | null>(null);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    if (!loadCatalog) return;
    let active = true;
    void getPublicPlatformBillingCatalog().then((catalog) => { if (active) setLoaded(catalog.creditUsage ?? null); }).catch(() => undefined);
    return () => { active = false; };
  }, [loadCatalog]);
  const examples = (usage ?? loaded)?.cohortMealPlans.examples;
  const details = <>
    <p className="mt-2">Professional lets you generate a reusable plan once and assign it to multiple clients. Reusing or assigning a saved plan uses no generation credits. AI edits and regeneration use additional credits.</p>
    {examples?.length ? <table className="mt-3 w-full text-left text-xs sm:text-sm">
      <thead><tr><th className="py-2 pr-2">Pattern length</th><th className="pr-2">Credit price</th><th>Available to start</th></tr></thead>
      <tbody>{examples.map((example) => <tr key={example.days} className="border-t border-sky-100">
        <td className="py-2 pr-2">{example.days === 2 ? '1–2' : example.days} days</td>
        <td className="pr-2">{example.reservationCredits}</td><td>{example.reservationCredits}</td>
      </tr>)}</tbody>
    </table> : <p className="mt-2">Check the current credit price before generating your plan.</p>}
    <p className="mt-2 text-xs">Review the fixed credit price before starting. It includes internal retries; failed generation releases the reservation. Assigning the saved plan to clients does not add a generation charge.</p>
  </>;
  return <section data-testid="cohort-credit-guide" className="my-4 rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-slate-700">
    {collapsible ? (
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between gap-3 bg-transparent p-0 text-left">
          <h3 className="font-semibold text-slate-950">Cohort plans: AI credit prices</h3>
          <ChevronDown className={`h-4 w-4 shrink-0 text-slate-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
        </CollapsibleTrigger>
        <CollapsibleContent>{details}</CollapsibleContent>
      </Collapsible>
    ) : <>
      <h3 className="font-semibold text-slate-950">Cohort plans: AI credit prices</h3>
      {details}
    </>}
  </section>;
}
